import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

interface ExtendDeadlineParams {
  assessmentId: string;
  endDate: string; // ISO 8601 format: "2023-11-03T16:00+01:00"
}

/**
 * Hook for extending assessment deadline
 */
export const useExtendAssessmentDeadline = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ assessmentId, endDate }: ExtendDeadlineParams) => {
      const response = await apiClient.post(
        '/assessment/extend-deadline/',
        {
          assessment_id: assessmentId,
          end_date: endDate,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      openToast({
        type: 'success',
        show: true,
        heading: 'Success',
        text: 'Assessment deadline extended successfully',
      });
      // Invalidate and refetch jobs list
      queryClient.invalidateQueries({ queryKey: ['manageJobs'] });
    },
    onError: (error: any) => {
      openToast({
        type: 'error',
        show: true,
        heading: 'Error',
        text: error?.response?.data?.message || 'Failed to extend assessment deadline',
      });
    },
  });

  return {
    extendDeadline: mutation.mutate,
    isLoading: mutation.isPending,
  };
};
